export interface QuoteScheduler<P> {
  /** Replaces whatever was pending or in flight: only the last request may answer. */
  schedule: (payload: P, immediate: boolean) => void;
  cancel: () => void;
}

export interface QuoteSchedulerOptions<P, R> {
  delayMs: number;
  run: (payload: P, signal: AbortSignal) => Promise<R>;
  onResult: (result: R, payload: P) => void;
  onError: (error: unknown, payload: P) => void;
}

/** Debounced, last-wins runner: a burst of keystrokes costs one request and a stale answer is dropped. */
export function createQuoteScheduler<P, R>({
  delayMs,
  run,
  onResult,
  onError,
}: QuoteSchedulerOptions<P, R>): QuoteScheduler<P> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let controller: AbortController | null = null;
  let sequence = 0;

  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    controller?.abort();
    controller = null;
    sequence += 1;
  };

  const fire = (payload: P) => {
    const current = ++sequence;
    const own = new AbortController();
    controller = own;
    run(payload, own.signal).then(
      (result) => {
        if (current === sequence) onResult(result, payload);
      },
      (error: unknown) => {
        if (current === sequence && !own.signal.aborted) onError(error, payload);
      },
    );
  };

  return {
    schedule(payload, immediate) {
      cancel();
      if (immediate) {
        fire(payload);
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        fire(payload);
      }, delayMs);
    },
    cancel,
  };
}

export interface RequestedQuote {
  signature: string;
  tick: number;
}

/** The same amount is priced again only when someone asks again (a click, an expiry), never on focus or blur. */
export function isNewQuoteRequest(previous: RequestedQuote | null, signature: string, tick: number): boolean {
  return !previous || previous.signature !== signature || previous.tick !== tick;
}
